/**
 * Top-level sidebar groups. Each group bundles one or more existing
 * NavigationService categories (modules) under a single rail entry, matching
 * the Zenflow design: Masters → Procurement → Inventory → Sales & Services →
 * Reports → Administration.
 *
 * Only presentation lives here. Visibility is still decided per item by
 * NavigationService (RAC permissions / legacy roles), and a group disappears
 * when none of its categories survive that filter.
 */
export interface NavGroupDef {
  id: string;
  label: string;
  /** Key into NAV_GROUP_ICONS. */
  icon: string;
  /** NavigationService category ids, in display order. */
  categories: string[];
}

export const NAV_GROUPS: NavGroupDef[] = [
  { id: 'dashboards', label: 'Dashboards', icon: 'reports', categories: ['dashboard'] },
  { id: 'masters', label: 'Masters', icon: 'products', categories: ['products', 'vendors'] },
  {
    id: 'procurement',
    label: 'Procurement',
    icon: 'purchase',
    categories: ['purchase', 'indent', 'mbq', 'schemes'],
  },
  {
    id: 'inventory',
    label: 'Inventory',
    icon: 'stock',
    categories: ['grn', 'transfers', 'picklist', 'stock'],
  },
  { id: 'sales', label: 'Sales & Services', icon: 'b2b', categories: ['b2b', 'billing', 'services'] },
  { id: 'reports', label: 'Reports', icon: 'reports', categories: ['reports'] },
  {
    id: 'admin',
    label: 'Administration',
    icon: 'control',
    categories: ['terminals', 'controlpanel'],
  },
  { id: 'superadmin', label: 'Super Admin', icon: 'shield', categories: ['superadmin'] },
];

/** 24×24 stroke icon paths (1.6px, round caps) from the design system. */
export const NAV_GROUP_ICONS: Record<string, string> = {
  shield: 'M12 3 4.5 6v5c0 4.6 3.2 8.6 7.5 10 4.3-1.4 7.5-5.4 7.5-10V6zM9.5 12l2 2 3.5-4',
  b2b: 'M8.5 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM2.5 20v-1.5A4.5 4.5 0 0 1 7 14h3a4.5 4.5 0 0 1 4.5 4.5V20M16.5 5.5a3 3 0 0 1 0 5.8M18 14a4 4 0 0 1 3.5 4v2',
  products: 'M3 8.5 12 4l9 4.5v7L12 20l-9-4.5zM3 8.5 12 13l9-4.5M12 13v7',
  vendors: 'M4 21V4h11v17M15 9h5v12M4 21h17M7.5 8h4M7.5 12h4M7.5 16h4',
  billing: 'M5 3h14v18l-3-2-2 2-2-2-2 2-2-2-3 2zM9 8h6M9 12h6M9 16h3',
  purchase:
    'M6 6h15l-1.5 9h-12zM6 6 5 3H2M9 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2zM18 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2z',
  grn: 'M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4M10 8l4 4-4 4M14 12H3',
  transfers: 'M4 6h5l11 12h-4M20 6h-4L4 18h5M17 3l3 3-3 3M17 15l3 3-3 3',
  stock: 'M5 3h14v18H5zM9 3v4h6V3M9 12h6M9 16h4',
  mbq: 'M5 4h14v16H5zM9 11l2.2 2.2L16 8.5M9 4V2M15 4V2',
  indent: 'M4 6h.01M4 12h.01M4 18h.01M9 6h11M9 12h11M9 18h7',
  picklist: 'M3 6h18v12H3zM3 10h18M7 14h5',
  services: 'M14.7 6.3a4 4 0 1 0 5 5L21 21H10L4.5 15.5a2.1 2.1 0 0 1 3-3L10 15',
  schemes:
    'M19 5 5 19M7.5 9a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zM16.5 20a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
  reports: 'M3 20h18M4 16l5-6 4 3 6.5-8',
  account: 'M6 2h12v20H6zM12 18h.01M9.5 6h5',
  integrations: 'M9 3v6M15 3v6M7 9h10v3a5 5 0 0 1-10 0zM12 17v4',
  control:
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-2.9 1.2v.1a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-2.9-1.2l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.7 1.7 0 0 0 3 15a2 2 0 1 1 0-4 1.7 1.7 0 0 0 1.4-2.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1A1.7 1.7 0 0 0 10 4V3.9a2 2 0 1 1 4 0V4a1.7 1.7 0 0 0 2.9 1.4l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1A1.7 1.7 0 0 0 21 11a2 2 0 1 1 0 4z',
};
